import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Input } from './ui/input';
import {
  Upload,
  Camera,
  FileText,
  FileSpreadsheet,
  File,
  Trash2,
  Download,
  Image,
  Loader2,
  X,
  Maximize2,
} from 'lucide-react';
import { toast } from 'sonner';
import { leadApi, projectApi } from '../lib/api';

const CATEGORY_OPTIONS = [
  { value: 'site_photos', label: 'Site Photos', icon: Camera },
  { value: 'estimates', label: 'Estimates', icon: FileSpreadsheet },
  { value: 'proposals', label: 'Proposals', icon: FileText },
  { value: 'other', label: 'Other', icon: File },
];

const CATEGORY_COLORS = {
  site_photos: 'bg-blue-600',
  estimates: 'bg-amber-600',
  proposals: 'bg-purple-600',
  other: 'bg-slate-600',
};

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImageType(contentType) {
  return contentType?.startsWith('image/');
}

function getFileIcon(contentType) {
  if (contentType?.startsWith('image/')) return Image;
  if (contentType?.includes('pdf')) return FileText;
  if (contentType?.includes('spreadsheet') || contentType?.includes('excel') || contentType?.includes('csv')) return FileSpreadsheet;
  return File;
}

export default function LeadFiles({ leadId, projectId }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [uploadCategory, setUploadCategory] = useState('site_photos');
  const [uploadDescription, setUploadDescription] = useState('');
  const fileInputRef = useRef(null);

  // Determine which API to use based on entity type
  const entityId = projectId || leadId;
  const api = projectId ? projectApi : leadApi;

  useEffect(() => {
    loadFiles();
  }, [entityId, filterCategory]);

  const loadFiles = async () => {
    try {
      const category = filterCategory === 'all' ? undefined : filterCategory;
      const res = await api.getFiles(entityId, category);
      setFiles(res.data);
    } catch (err) {
      console.error('Failed to load files:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    setUploading(true);
    let successCount = 0;

    for (const file of selectedFiles) {
      if (file.size > 100 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 100MB limit`);
        continue;
      }
      try {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
        if (file.size > 5 * 1024 * 1024) {
          setUploadStatus(`Uploading ${file.name} (${sizeMB}MB)...`);
        }
        await api.uploadFile(entityId, file, uploadCategory, uploadDescription);
        successCount++;
      } catch (err) {
        const detail = err.response?.data?.detail || err.message || 'Upload failed — try again or use a smaller file';
        toast.error(`Failed to upload ${file.name}: ${detail}`);
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} file${successCount > 1 ? 's' : ''} uploaded`);
      setUploadDescription('');
      loadFiles();
    }
    setUploading(false);
    setUploadStatus('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (fileId, filename) => {
    if (!window.confirm(`Delete "${filename}"?`)) return;
    try {
      await api.deleteFile(entityId, fileId);
      setFiles(files.filter(f => f.id !== fileId));
      toast.success('File deleted');
    } catch (err) {
      toast.error('Failed to delete file');
    }
  };

  const handleDownload = async (file) => {
    try {
      const res = await api.getFileUrl(entityId, file.id);
      window.open(res.data.download_url, '_blank');
    } catch {
      if (file.download_url) window.open(file.download_url, '_blank');
    }
  };

  const handleViewOriginal = async (file) => {
    try {
      const res = await api.getOriginalUrl(entityId, file.id);
      window.open(res.data.download_url, '_blank');
    } catch {
      toast.error('Full resolution not available');
    }
  };

  const categoryCounts = files.reduce((acc, f) => {
    acc[f.category] = (acc[f.category] || 0) + 1;
    return acc;
  }, {});

  return (
    <Card className="rounded-sm" data-testid="lead-files-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            Files & Documents
            {files.length > 0 && (
              <Badge variant="secondary" className="ml-1">{files.length}</Badge>
            )}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Upload Section */}
        <div className="p-3 sm:p-4 border-2 border-dashed rounded-sm border-muted-foreground/20 hover:border-primary/40 transition-colors" data-testid="file-upload-zone">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row gap-2">
              <Select value={uploadCategory} onValueChange={setUploadCategory}>
                <SelectTrigger className="w-full sm:w-40 rounded-sm" data-testid="upload-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                placeholder="Description (optional)"
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                className="rounded-sm flex-1"
                data-testid="upload-description-input"
              />
            </div>
            <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  accept="image/*,.dng,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                  data-testid="file-input"
                />
                <Button
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="rounded-sm bg-orange-500 hover:bg-orange-600"
                  data-testid="upload-files-btn"
                >
                  {uploading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {uploadStatus || 'Uploading...'}</>
                  ) : (
                    <><Upload className="h-4 w-4 mr-2" /> Upload</>
                  )}
                </Button>
                <span className="text-xs text-muted-foreground hidden sm:inline">Max 100MB per file</span>
              </div>
          </div>
        </div>

        {/* Category Filter */}
        {files.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <Badge
              variant={filterCategory === 'all' ? 'default' : 'outline'}
              className="cursor-pointer"
              onClick={() => setFilterCategory('all')}
            >
              All ({files.length})
            </Badge>
            {CATEGORY_OPTIONS.map(c => (
              categoryCounts[c.value] > 0 && (
                <Badge
                  key={c.value}
                  variant={filterCategory === c.value ? 'default' : 'outline'}
                  className={`cursor-pointer ${filterCategory === c.value ? CATEGORY_COLORS[c.value] : ''}`}
                  onClick={() => setFilterCategory(c.value)}
                >
                  {c.label} ({categoryCounts[c.value]})
                </Badge>
              )
            ))}
          </div>
        )}

        {/* File List */}
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : files.length === 0 ? (
          <p className="text-center text-muted-foreground py-6 text-sm">
            No files uploaded yet. Upload site photos, estimates, or proposals.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {files.map(file => {
              const FileIcon = getFileIcon(file.content_type);
              const isImage = isImageType(file.content_type);

              return (
                <div
                  key={file.id}
                  className="group p-3 border rounded-sm hover:border-primary/30 transition-colors"
                  data-testid={`file-card-${file.id}`}
                >
                  {/* Preview / Icon */}
                  <div className="mb-2">
                    {isImage && file.download_url ? (
                      <div className="relative aspect-video bg-muted rounded-sm overflow-hidden cursor-pointer" onClick={() => handleDownload(file)}>
                        <img
                          src={file.download_url}
                          alt={file.filename}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                    ) : (
                      <div
                        className="aspect-video bg-muted rounded-sm flex items-center justify-center cursor-pointer"
                        onClick={() => handleDownload(file)}
                      >
                        <FileIcon className="h-10 w-10 text-muted-foreground" />
                      </div>
                    )}
                  </div>

                  {/* File Info */}
                  <div className="space-y-1">
                    <p className="text-sm font-medium truncate" title={file.filename}>{file.filename}</p>
                    {file.description && (
                      <p className="text-xs text-muted-foreground truncate">{file.description}</p>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge className={`text-xs ${CATEGORY_COLORS[file.category]}`}>
                          {CATEGORY_OPTIONS.find(c => c.value === file.category)?.label || file.category}
                        </Badge>
                        <span className="text-xs text-muted-foreground">{formatFileSize(file.file_size)}</span>
                      </div>
                      <div className="flex gap-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                        {file.has_original && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-1.5 text-xs text-blue-600 hover:text-blue-700"
                            onClick={() => handleViewOriginal(file)}
                            title={`View full resolution${file.original_size ? ` (${formatFileSize(file.original_size)})` : ''}`}
                            data-testid={`fullres-btn-${file.id}`}
                          >
                            <Maximize2 className="h-3.5 w-3.5 mr-1" />
                            Full Res
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0"
                          onClick={() => handleDownload(file)}
                          data-testid={`download-btn-${file.id}`}
                        >
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                          onClick={() => handleDelete(file.id, file.filename)}
                          data-testid={`delete-btn-${file.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {file.uploaded_by_name} · {new Date(file.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
